import { Box, Typography, Button } from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import React, { useEffect, useState, useRef } from "react";
import { defaultParams } from "../../App";
import {
  Frequency,
  SimulationFormInputs,
  SimulationFormParams,
  SimulationFormProps,
} from "../../types/forms";
import AcrInputs from "./AcrInputs";
import { CurrencyField } from "./CurrencyField";
import FormWrapper from "./FormWrapper";
import MortgageTypeInput from "./MortgageTypeInput";
import RepaymentInputs from "./RepaymentInputs";
import SavingsInputs from "./SavingsInputs";
import SubmitButton from "./SubmitButton";

const demoParams: SimulationFormInputs = {
  principal: 250000.0,
  interestOnly: false,
  termYears: 25,
  interestRate: 4.5,
  desiredMonthlyPayment: undefined,
  additionalCapitalRepayments: true,
  dealLength: 24,
  maxAffordable: 2000.0,
  savingsInterestRate: 3.5,
  saveAtStart: false,
  additionalCapitalRepaymentPercentageLimit: 10,
  reduceTermOnAdditionalCapitalRepayment: false,
  savingsAccounts: [
    {
      name: "Regular Savings",
      initialAmount: 5000,
      interestRate: 2.0,
      contributionAmount: 200,
      contributionFrequency: "monthly" as Frequency,
      interestCompounding: "monthly" as Frequency,
    },
    {
      name: "Investment Account",
      initialAmount: 10000,
      interestRate: 9.5,
      contributionAmount: 100,
      contributionFrequency: "monthly" as Frequency,
      interestCompounding: "yearly" as Frequency,
    },
  ],
};

export default function SimulationForm({
  model,
  onSubmit,
  onCancel,
  isEditing,
  editingIndex,
}: SimulationFormProps) {
  const [localIndex, setLocalIndex] = useState<number | null>(
    isEditing ? (editingIndex ?? null) : null,
  );
  const [isInitialized, setIsInitialized] = useState<boolean>(
    isEditing ?? false,
  );
  const hasInitialized = useRef<boolean>(false);

  useEffect(() => {
    if (!isEditing && !hasInitialized.current) {
      hasInitialized.current = true;
      const newIndex = model.simulations.length;
      const newSim: SimulationFormParams = {
        inputs: defaultParams,
        updated: true,
      };
      model.addSimulation(newSim);
      setLocalIndex(newIndex);
      setIsInitialized(true);
    } else if (isEditing && !hasInitialized.current) {
      hasInitialized.current = true;
      setIsInitialized(true);
    }
    // This should only run once when the form is mounted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = () => {
    if (!isEditing && localIndex != null) {
      model.removeSimulation(localIndex);
    }
    onCancel();
  };

  const handleDemo = () => {
    if (activeIndex != null) {
      // Replace current simulation with demo parameters
      model.updateSimulation(activeIndex, {}, demoParams);
    }
  };

  // Wait for initialization before rendering form
  if (!isInitialized) {
    return null;
  }

  // Determine which index to use
  const activeIndex =
    isEditing && editingIndex != null ? editingIndex : localIndex;

  // Validate that the simulation still exists - if not, the state is stale
  if (activeIndex == null || !model.simulations[activeIndex]) {
    console.warn(
      "Form rendered with stale state - simulation no longer exists",
      {
        activeIndex,
        isEditing,
        editingIndex,
        localIndex,
        simulationsLength: model.simulations.length,
      },
    );
    // Return null to prevent errors - the parent should handle showing the correct view
    return null;
  }

  const formInputs: SimulationFormInputs =
    model.simulations[activeIndex].inputs;

  const handleChange = (updates: Partial<SimulationFormInputs>) => {
    if (activeIndex != null) {
      model.updateSimulation(activeIndex, {}, updates);
    }
  };

  return (
    <FormWrapper onSubmit={onSubmit}>
      {(error) => (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">
              {isEditing ? "Update Simulation" : "Create Simulation"}
            </Typography>
            <Button
              startIcon={<AutoFixHighIcon />}
              onClick={handleDemo}
              variant="outlined"
              size="small"
            >
              Demo
            </Button>
          </Box>

          <CurrencyField
            label="Mortgage Debt"
            value={formInputs.principal}
            onChange={(val) => handleChange({ principal: val })}
            fullWidth
            margin="normal"
            required
            greaterThanZero
          />

          <MortgageTypeInput
            interestOnly={formInputs.interestOnly}
            interestRate={formInputs.interestRate}
            desiredMonthlyPayment={formInputs.desiredMonthlyPayment}
            onChange={handleChange}
          />

          <RepaymentInputs
            interestOnly={formInputs.interestOnly}
            termYears={formInputs.termYears}
            interestRate={formInputs.interestRate}
            desiredMonthlyPayment={formInputs.desiredMonthlyPayment}
            onChange={handleChange}
          />

          <AcrInputs
            additionalCapitalRepayments={formInputs.additionalCapitalRepayments}
            dealLength={formInputs.dealLength}
            maxAffordable={formInputs.maxAffordable}
            savingsInterestRate={formInputs.savingsInterestRate}
            saveAtStart={formInputs.saveAtStart}
            additionalCapitalRepaymentPercentageLimit={
              formInputs.additionalCapitalRepaymentPercentageLimit
            }
            reduceTermOnAdditionalCapitalRepayment={
              formInputs.reduceTermOnAdditionalCapitalRepayment
            }
            skipAdditionalCapitalRepayments={
              formInputs.skipAdditionalCapitalRepayments
            }
            interestOnly={formInputs.interestOnly}
            onChange={handleChange}
          />

          <SavingsInputs
            accounts={formInputs.savingsAccounts}
            onChange={handleChange}
          />

          <SubmitButton
            isEditing={isEditing}
            error={error}
            onCancel={handleCancel}
          />
        </>
      )}
    </FormWrapper>
  );
}
